import React, { Fragment } from "react";
import {
  Box,
  Collapse,
  Flex,
  Grid,
  GridItem,
  Image,
  Text,
} from "@chakra-ui/react";
import useGetClaimList from "@/features/my-order/features/order-history/features/claim/hooks/useGetClaimList";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { ClaimProduct } from "@/features/my-order/features/order-history/features/claim/types";
import { useClaimDrawerStore } from "@/features/my-order/features/order-history/stores/useClaimDrawerStore";
import { useSetRecoilState } from "recoil";
import claimDetailIdStore from "@/features/my-order/features/order-history/stores/useClaimDetailIdStore";
import { OrderHistoryClaimDetailDrawerWrapper } from "@/features/my-order/features/order-history/features/claim";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import ClaimStatusBadge from "@/features/my-order/components/ClaimStatusBadge";

function OrderHistoryClaimContent() {
  const { data } = useGetClaimList();

  return (
    <Flex paddingTop={"4px"} flexDirection={"column"}>
      <InfoGrid claimProducts={data?.claimProducts ?? []} />
    </Flex>
  );
}

export default OrderHistoryClaimContent;

const InfoGrid = ({ claimProducts }: { claimProducts: ClaimProduct[] }) => {
  const { tTitle, isKor } = useI18nHelper();
  const { onOpen } = useClaimDrawerStore();
  const setClaimDetailId = useSetRecoilState(claimDetailIdStore);

  const [isExpanded, setIsExpanded] = React.useState(false);

  const visibleClaimProducts = claimProducts.slice(0, 3);
  const collapsibleClaimProducts = claimProducts.slice(3);

  const renderRow = (product: ClaimProduct) => (
    <Grid
      key={product.id}
      gridColumn={"1 / -1"}
      templateColumns={"240px 1fr 200px"}
      _hover={{ background: "#FCFCFB" }}
      _active={{
        background: "#F0F0F0",
      }}
      transition={"background 0.2s"}
      cursor={"pointer"}
      marginTop={"1px"}
      onClick={() => {
        setClaimDetailId(product.id);
        onOpen();
      }}
    >
      <GridItem
        padding={"10px 12px"}
        boxShadow={"0 0.5px 0 0 #D4D4D1"}
        height={"80px"}
      >
        <Flex gap={"8px"} alignItems={"center"}>
          <Box
            width={"60px"}
            height={"60px"}
            minWidth={"60px"}
            borderRadius={"4px"}
            overflow={"hidden"}
            boxShadow={"0 0 0 0.5px #D4D4D1"}
          >
            <Image
              src={product.imageUrl}
              alt={product.sku}
              fallbackSrc={"/images/placeholder-product.png"}
              objectFit={"cover"}
              width={"100%"}
              height={"100%"}
            />
          </Box>

          <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
            <Text textStyle={"body-sm-medium"} color={"#444444"} noOfLines={1}>
              {product.brandName}
            </Text>
            <Text textStyle={"body-md-bold"} color={"#444444"} noOfLines={1}>
              {product.sku}
            </Text>
            <Flex gap={"4px"} alignItems={"center"}>
              <Text
                textStyle={"caption-medium"}
                color={"#6F6F6C"}
                noOfLines={1}
              >
                {product.size}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </GridItem>

      <GridItem
        padding={"10px 12px"}
        boxShadow={"0 0.5px 0 0 #D4D4D1"}
        height={"80px"}
      >
        <Flex alignItems={"center"} height={"100%"}>
          <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {tTitle("order_history:claim.type")} :{" "}
              {tTitle(
                `order_history:claim.type_${product.requestType.toLowerCase()}_title`
              )}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#444444"} noOfLines={2}>
              {product.requestContent}
            </Text>
          </Flex>
        </Flex>
      </GridItem>

      <GridItem
        padding={"10px 12px"}
        boxShadow={"0 0.5px 0 0 #D4D4D1"}
        height={"80px"}
      >
        <Flex
          flexDirection={"column"}
          height={"100%"}
          justifyContent={"center"}
          gap={"2px"}
          minWidth={0}
        >
          <ClaimStatusBadge status={product.status} />
          {product.responseContent && (
            <Text textStyle={"body-sm-medium"} color={"#444444"} noOfLines={2}>
              {product.responseContent}
            </Text>
          )}
        </Flex>
      </GridItem>
    </Grid>
  );

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      <Box
        border={"1px solid #D4D4D1"}
        borderRadius={"6px"}
        overflow={"hidden"}
      >
        <Grid
          templateColumns={"240px 1fr 200px"}
          width={"100%"}
          alignItems={"start"}
        >
          {/* Header */}
          <GridItem
            background={"#FCFCFB"}
            paddingX={"12px"}
            boxShadow={"0 0.5px 0 0 #D4D4D1"}
          >
            <Text
              textStyle={"body-sm-medium"}
              color={"#6F6F6C"}
              paddingY={"8px"}
            >
              {tTitle("order_history:claim.product_info")}
            </Text>
          </GridItem>

          <GridItem
            background={"#FCFCFB"}
            paddingX={"12px"}
            boxShadow={"0 0.5px 0 0 #D4D4D1"}
          >
            <Text
              textStyle={"body-sm-medium"}
              color={"#6F6F6C"}
              paddingY={"8px"}
            >
              {tTitle("order_history:claim.claim_reason")}
            </Text>
          </GridItem>

          <GridItem
            background={"#FCFCFB"}
            paddingX={"12px"}
            boxShadow={"0 0.5px 0 0 #D4D4D1"}
          >
            <Text
              textStyle={"body-sm-medium"}
              color={"#6F6F6C"}
              paddingY={"8px"}
            >
              {tTitle("order_history:claim.process_content")}
            </Text>
          </GridItem>

          {/* 항상 보이는 3개 */}
          {visibleClaimProducts.map(renderRow)}
        </Grid>

        {/* 4번째 이후만 접힘/펼침 */}
        <Collapse in={isExpanded} animateOpacity={true}>
          <Grid
            templateColumns={"240px 1fr 200px"}
            width={"100%"}
            alignItems={"start"}
          >
            {collapsibleClaimProducts.map(renderRow)}
          </Grid>
        </Collapse>

        <OrderHistoryClaimDetailDrawerWrapper />
      </Box>

      {claimProducts.length > 3 && (
        <Flex
          gap={"4px"}
          alignItems={"center"}
          justifyContent={"center"}
          cursor={"pointer"}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <Text textStyle={"body-sm-bold"}>
            {!isExpanded
              ? `${claimProducts.length - 3}${isKor ? "개" : ""} ${tTitle(
                  "expand"
                )}`
              : tTitle("fold_more")}
          </Text>
          <CustomIcon
            src={isExpanded ? "chevron-up" : "chevron-down"}
            width={16}
            height={16}
            color={"#8F8F8C"}
          />
        </Flex>
      )}
    </Flex>
  );
};
